import React, { useEffect, useState, Dispatch, SetStateAction } from "react";
import { Edit, Settings } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  CircularProgress,
  keyframes,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import SelectTableHead, { HeadCell } from "./SelectTableHead";
import CustomTextField from "./CustomTextField";
import type { ManagedItem } from "../types";

const commonTableCellStyles = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  // fontWeight: 500,
  fontSize: 16,
  letterSpacing: "0.01071em",
  display: "table-cell",
  verticalAlign: "inherit",
  borderBottom: "1px solid rgba(224, 224, 224, 1)",
  textAlign: "center",
  padding: "16px",
  paddingTop: "20px",
  paddingBottom: "20px",
  color: "rgba(0, 0, 0, 0.87)",
  textOverflow: "ellipsis",
  overflow: "hidden",
  whiteSpace: "nowrap",
  maxWidth: "110px",
  borderLeft: "1px solid rgb(224, 224, 224)",
  borderTop: "1px solid rgb(224, 224, 224)",
};


type CheckboxSelection = {
  [key: string]: boolean;
};

type ProductTableProps = {
  total: number;
  items: ManagedItem[];
  companyCd: string;
  isUpdateDisplay: boolean;
  editedPrices: Record<string, number | null>;
  selectedIds: number[];
  isSelectedAll: boolean;
  isIndeterminate: boolean;
  isLoading: boolean;
  isMoreLoading: boolean;
  isSelected: (rowId: number) => boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setIsMoreLoading: Dispatch<SetStateAction<boolean>>;
  toggleSelectedAll: () => void;
  toggleSelected: (id: number) => void;
  handleWholesalePriceChange: (itemCd: string, newPrice: number) => void;
  onDataChange: (data: ManagedItem[]) => void;
};

/**
 * ProductTableコンポーネントは、倉庫情報をテーブル形式で表示します。
 * @param {object} props - コンポーネントのプロパティ。
 * @param {Function} props.onChange - 都道府県の選択が変更された際に呼ばれるコールバック関数。
 */
function ProductTable({
  total,
  items,
  companyCd,
  isUpdateDisplay,
  editedPrices,
  selectedIds,
  isSelectedAll,
  isIndeterminate,
  isLoading,
  isMoreLoading,
  setIsLoading,
  setIsMoreLoading,
  isSelected,
  toggleSelectedAll,
  toggleSelected,
  handleWholesalePriceChange,
  onDataChange,
}: ProductTableProps) {
  // const companys = [
  //   {
  //     companyName: "コバック",
  //     companyCd: "001",
  //     centerCd: "001",
  //   },
  // ];

  // const products = [
  //   {
  //     productCd: "000001",
  //     janCd: "000000000001",
  //     className: "タイヤ",
  //     productSrc: "ブリヂストン",
  //     brand: "ボテンザ",
  //     productName: "WH STA",
  //     size: "185/65R/15H",
  //     joyfulwholesalePrice: "1000",
  //   },
  // ]; // フックからデータを取得
  // const [tableData, setTableData] = useState(items);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<CheckboxSelection>({});

  // useEffect(() => {
  //   if (items.length > 0) {
  //     // 初期データとしてテーブルをセット
  //     setTableData(items);
  //   }
  //   setIsLoading(false);
  // }, [items]);

  useEffect(() => {
    const updatedData = items.map((item) => ({
      ...item,
      deletionFlag: selectedCheckboxes[item.item_cd] || false,
    }));

    const modifiedData = updatedData.filter((data) => {
      const originalData = items.find((s) => s.item_cd === data.item_cd);
      if (!originalData) return false;
      return true;
    });

    // 変更があったデータを親コンポーネントに渡す
    onDataChange(modifiedData);
  }, [selectedCheckboxes, items, onDataChange]);

  const headCells: HeadCell[] = [
    {
      id: "productCd",
      label: "商品コード",
    },
    {
      id: "janCd",
      label: "JANコード",
    },
    {
      id: "className",
      label: "大分類",
    },
    {
      id: "productSrc",
      label: "仕入先名",
    },
    {
      id: "brand",
      label: "ブランド",
    },
    {
      id: "productName",
      label: "商品名",
    },
    {
      id: "size",
      label: "サイズ",
    },
    {
      id: "joyfulwholesalePrice",
      label: "ジョイフル仕切単価",
    },
  ];

  // const {
  //   selectedRowIds: selectedIds,
  //   isSelected,
  //   isSelectedAll,
  //   isIndeterminate,
  //   toggleSelected,
  //   toggleSelectedAll,
  // } = useRowSelect(items.map((item) => parseInt(item.item_cd, 10)));

  // const selectedItems = items.filter((item) => selectedIds.includes(parseInt(item.item_cd, 10)));

  const glow = keyframes`
    0% { box-shadow: 0 0 0px rgba(231, 255, 73, 0.6); }
    50% { box-shadow: 0 0 15px rgba(231, 255, 73, 1); }
    100% { box-shadow: 0 0 0px rgba(231, 255, 73, 0.6); }
  `;

  const classCdToName = (classCd: string) => {
    if (classCd === "02") return "タイヤ"
    if (classCd === "03") return "ホイール"
    return classCd;
  }

  const tableBodyElement = () => (
    <>
      {items.map((it) => {
        const idNum = parseInt(it.item_cd, 10);
        const isItemSelected = selectedIds.includes(idNum);
        return (
          <TableBody>
            <TableRow
              key={it.item_cd}
              role="checkbox"
              tabIndex={-1}
              selected={isItemSelected}
              hover
            >
              <TableCell padding="checkbox" sx={{ paddingLeft: 2, paddingRight: 2, textAlign: "center" }}>
                <Checkbox
                  color="secondary"
                  checked={isSelected(idNum)}
                  onChange={() => toggleSelected(idNum)}
                />
              </TableCell>
              <TableCell sx={{ ...commonTableCellStyles, maxWidth: 50 }}>{it.item_cd}</TableCell>
              <TableCell sx={{ ...commonTableCellStyles, maxWidth: 200 }}>{it.jan_cd}</TableCell>
              <TableCell sx={{ ...commonTableCellStyles, maxWidth: 50 }}>{classCdToName(it.class_name)}</TableCell>
              <TableCell sx={{ ...commonTableCellStyles, maxWidth: 50 }}>
                {it.vendor_name}
              </TableCell>
              <TableCell sx={{ ...commonTableCellStyles, maxWidth: 50 }}>{it.brand}</TableCell>
              <TableCell sx={{ ...commonTableCellStyles, maxWidth: 50 }}>{it.item_name}</TableCell>
              <TableCell sx={{ ...commonTableCellStyles, maxWidth: 50 }}>{it.size}</TableCell>
              {isUpdateDisplay ? (
                <TableCell sx={{ ...commonTableCellStyles, maxWidth: 50 }}>
                  <CustomTextField
                    id="warehouseOne"
                    type="text"
                    label="仕切単価"
                    text={
                      // item.store_wholesale_price?.toString() ??
                      editedPrices[it.item_cd]?.toString() ?? ""
                    }
                    setText={(newValue) => handleWholesalePriceChange(it.item_cd, parseInt(newValue, 10))}
                    maxLength={12}
                    size="small"
                    isNumericText
                  />
                </TableCell>
              ) : (
                <TableCell sx={{ ...commonTableCellStyles, maxWidth: 50 }}>
                  {it.store_wholesale_price}
                </TableCell>
              )}
            </TableRow>
          </TableBody>
        );
      })}
    </>
  );

  return (
    <div>
      {isLoading ? (
        <Box py={3} textAlign="center">
          <CircularProgress />
        </Box>
      ) : (
        <div>
          {/* フォームタイトル */}
          <Box
            mt={3}
            display="flex"
            borderBottom={4}
            borderColor="primary.main"
            width="100%"
            justifyContent="space-between"
          >
            {/* <Box display="flex" flexDirection="row" justifyContent="space-between" width="100%">
                  <Box display="flex" flexDirection="row"> */}
            <Box display="flex">
              <Typography fontSize={20}>検索結果：</Typography>
              <Typography fontSize={20}>{total}件</Typography>
            </Box>
            <Box display="flex">
              <Typography fontSize={20}>企業コード：{companyCd}</Typography>
            </Box>
          </Box>

          <Box mt={2}>
            <TableContainer component={Paper} sx={{ overflowX: "auto", width: "100%" }}>
              <Table sx={{ width: "100%", borderCollapse: "separate" }} aria-label="mock table">
                <SelectTableHead
                  onSelectAllClick={(e) => {
                    toggleSelectedAll();
                  }}
                  headCells={headCells}
                  checked={isSelectedAll}
                  indeterminate={isIndeterminate}
                />
                {tableBodyElement()}
              </Table>
              {isMoreLoading && (
                <Box py={3} textAlign="center">
                  <CircularProgress />
                </Box>
              )}
            </TableContainer>
          </Box>
        </div>
      )}
    </div>
  );
}

export default React.memo(ProductTable);
